import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/i18n/useLanguage";
import { dayHeading, formatInTz } from "./dateUtils";
import { EventRow } from "./EventRow";
import type { CalendarEventListItem } from "./types";

interface AgendaListProps {
  events: CalendarEventListItem[];
  today: Date;
  tz: string;
  selectedEventId: string | null;
  onSelect: (id: string) => void;
  /** Optional list of YYYY-MM-DD dates with NO events to show as placeholders. */
  emptyDays?: string[];
}

function groupByDate(events: CalendarEventListItem[]): Map<string, CalendarEventListItem[]> {
  const m = new Map<string, CalendarEventListItem[]>();
  for (const ev of events) {
    const list = m.get(ev.scheduled_date) ?? [];
    list.push(ev);
    m.set(ev.scheduled_date, list);
  }
  return m;
}

function sortDayEvents(list: CalendarEventListItem[]): CalendarEventListItem[] {
  // All-day events (no scheduled_at_utc) sort first; timed events sort by
  // ascending time. Stable enough — same instant → preserves API order.
  return [...list].sort((a, b) => {
    if (!a.scheduled_at_utc && !b.scheduled_at_utc) return 0;
    if (!a.scheduled_at_utc) return -1;
    if (!b.scheduled_at_utc) return 1;
    return a.scheduled_at_utc.localeCompare(b.scheduled_at_utc);
  });
}

function NowLine({ tz, now }: { tz: string; now: Date }) {
  const time = formatInTz(now.toISOString(), tz, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return (
    <div
      className="my-1 flex items-center gap-2"
      role="separator"
      aria-label="current time"
      data-testid="now-line"
    >
      <div className="h-px flex-1 bg-red-500" />
      <span className="rounded-full bg-red-500/15 px-1.5 py-px font-mono text-[10px] tabular-nums text-red-400">
        {time}
      </span>
    </div>
  );
}

export function AgendaList({
  events,
  today,
  tz,
  selectedEventId,
  onSelect,
  emptyDays = [],
}: AgendaListProps) {
  const { t } = useTranslation("calendar");
  const { language } = useLanguage();
  const todayLabel = t("nav.today");
  const todayIso = today.toISOString().slice(0, 10);
  const todayRef = useRef<HTMLDivElement | null>(null);

  // Independent ticker so the line keeps moving even when the 30s data poll
  // returns identical content. 60s cadence is fine — the line is a coarse
  // visual marker, not a stopwatch.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const grouped = groupByDate(events);
  const allDates = new Set<string>([...grouped.keys(), ...emptyDays]);
  const orderedDates = [...allDates].sort();
  // Stable per visible date set — changes on week navigation, NOT on every
  // 30s poll (event content changes, dates don't). So the effect below
  // re-scrolls when the user navigates away and back to a week containing
  // today, but doesn't snap the scroll position on each refresh.
  const weekKey = orderedDates.join(",");

  // Auto-scroll today into view on first render AND on week navigation
  // when today is in the new visible set. If today isn't visible (e.g.
  // user is browsing a past week), todayRef.current is null and the
  // scroll silently skips.
  useEffect(() => {
    if (todayRef.current && typeof todayRef.current.scrollIntoView === "function") {
      todayRef.current.scrollIntoView({ block: "start", behavior: "auto" });
    }
  }, [weekKey]);

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto">
      {orderedDates.map((iso) => {
        const dayEvents = sortDayEvents(grouped.get(iso) ?? []);
        const isToday = iso === todayIso;
        const d = new Date(`${iso}T00:00:00Z`);
        // Position of the now-line within today's events: index of the
        // first event whose scheduled time is strictly after now. All-day
        // events sort first and never push the line down (treated as past
        // once today started). If nothing is in the future, the line goes
        // at the bottom (nowIndex === dayEvents.length).
        const nowMs = now.getTime();
        const nowIndex = isToday
          ? (() => {
              const idx = dayEvents.findIndex((ev) =>
                ev.scheduled_at_utc
                  ? new Date(ev.scheduled_at_utc).getTime() > nowMs
                  : false,
              );
              return idx === -1 ? dayEvents.length : idx;
            })()
          : -1;
        return (
          // id used by DayRail click → scrollIntoView, so the left rail
          // becomes a navigable index over the agenda.
          <div key={iso} id={`agenda-day-${iso}`}>
            <div
              ref={isToday ? todayRef : undefined}
              className="sticky top-0 z-10 bg-background py-1 text-xs font-medium text-foreground/80"
            >
              ▾ {dayHeading(d, today, language, todayLabel)}
            </div>
            {dayEvents.length === 0 ? (
              <>
                <div className="py-1 text-xs text-muted-foreground italic">
                  {t("event.noEventsShort")}
                </div>
                {isToday && <NowLine tz={tz} now={now} />}
              </>
            ) : (
              <div className="flex flex-col gap-1">
                {dayEvents.map((ev, i) => (
                  <Fragment key={ev.id}>
                    {isToday && i === nowIndex && <NowLine tz={tz} now={now} />}
                    <EventRow
                      event={ev}
                      tz={tz}
                      selected={ev.id === selectedEventId}
                      onSelect={onSelect}
                    />
                  </Fragment>
                ))}
                {isToday && nowIndex === dayEvents.length && (
                  <NowLine tz={tz} now={now} />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
